import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Wallet, RefreshCw, ChevronDown, ChevronRight, AlertTriangle,
  CheckCircle2, HelpCircle, Loader2, Package, Undo2, Search,
} from 'lucide-react'
import { reembolsos, ReembolsoBatch, ReembolsoLine, FacturaCod, FacturaCodStatus } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

const CARRIERS: Record<string, { label: string; color: string }> = {
  mrw: { label: 'MRW', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  gls: { label: 'GLS', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  cex: { label: 'Correos Express', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  ctt: { label: 'CTT', color: 'bg-slate-100 text-slate-600' },
}
const eur = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
const fdate = (s?: string | null) => (s ? s.slice(0, 10).split('-').reverse().join('/') : '—')

const STATUS: Record<FacturaCodStatus, { label: string; color: string }> = {
  impago: { label: 'Impago', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  devuelta: { label: 'Devuelta sin rectificativa', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  diferencia: { label: 'Importe distinto', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  en_gracia: { label: 'En plazo', color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  cobrada: { label: 'Cobrada', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  cobrada_historico: { label: 'Cobrada (histórico Factusol)', color: 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400' },
  anulada: { label: 'Anulada / rectificada', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  excluida: { label: 'Excluida (CTT)', color: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400' },
  resuelta: { label: 'Resuelta a mano', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300' },
}

type Tab = 'avisos' | 'facturas' | 'liquidaciones'

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  for (const k of ['reembolsos-dashboard', 'reembolsos-batches', 'reembolsos-batch', 'reembolsos-facturas',
    'reembolsos-revision', 'reembolsos-resolved']) {
    qc.invalidateQueries({ queryKey: [k] })
  }
}

export default function ReembolsosPage() {
  const [tab, setTab] = useState<Tab>('avisos')
  const toast = useToast()
  const qc = useQueryClient()

  const dash = useQuery({ queryKey: ['reembolsos-dashboard'], queryFn: reembolsos.dashboard })

  const refreshMut = useMutation({
    mutationFn: async () => {
      const a = await reembolsos.ingest(45)
      const b = await reembolsos.syncFactusol()
      return { a, b }
    },
    onSuccess: ({ a, b }) => {
      toast.success(`${a.nuevas_liquidaciones} liquidación(es) nueva(s) · ${b.facturas} facturas contra reembolso sincronizadas`)
      invalidateAll(qc)
    },
    onError: (e: Error) => toast.error(`Error al actualizar: ${e.message}`),
  })

  const r = dash.data?.resumen || {}
  const est = dash.data?.control?.por_estado || {}
  const n = (k: string) => est[k]?.count || 0
  const t = (k: string) => est[k]?.total || 0
  const revisionCount = dash.data?.control?.revision_count || 0
  const avisos = n('impago') + n('devuelta') + n('diferencia') + revisionCount

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Cabecera */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
            <Wallet className="w-6 h-6 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Control de Reembolsos</h1>
            <p className="text-sm text-slate-500">
              Facturas contra reembolso de Factusol frente a las liquidaciones de MRW, GLS y Correos Express
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={() => refreshMut.mutate()}
            disabled={refreshMut.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm font-medium"
          >
            {refreshMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Actualizar ahora
          </button>
          <span className="text-xs text-slate-400">
            Factusol: {fdate(dash.data?.ultima_sync_factusol)} · buzón: {fdate(dash.data?.ultimo_correo_procesado)}
          </span>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Kpi label="Impagos" value={String(n('impago'))} sub={eur(t('impago'))} warn={n('impago') > 0} />
        <Kpi label="Devueltas sin rectificativa" value={String(n('devuelta'))} sub={eur(t('devuelta'))} warn={n('devuelta') > 0} />
        <Kpi label="Diferencias / pagos sin factura" value={String(n('diferencia') + revisionCount)} warn={n('diferencia') + revisionCount > 0} />
        <Kpi label="En plazo de liquidación" value={String(n('en_gracia'))} sub={`${eur(t('en_gracia'))} · ${dash.data?.control?.grace_days ?? 15} días`} />
        <Kpi label="Liquidaciones sin confirmar en banco" value={String(r.pend_banco || 0)}
          sub={`${eur(r.pend_banco_total)}${r.pend_banco_antiguas ? ` · ${r.pend_banco_antiguas} con +7 días` : ''}`}
          warn={(r.pend_banco_antiguas || 0) > 0} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700 mb-4">
        <TabBtn active={tab === 'avisos'} onClick={() => setTab('avisos')}>
          Avisos {avisos > 0 && <span className="ml-1 text-xs bg-red-500 text-white rounded-full px-1.5">{avisos}</span>}
        </TabBtn>
        <TabBtn active={tab === 'facturas'} onClick={() => setTab('facturas')}>Facturas</TabBtn>
        <TabBtn active={tab === 'liquidaciones'} onClick={() => setTab('liquidaciones')}>Liquidaciones</TabBtn>
      </div>

      {tab === 'avisos' && <Avisos />}
      {tab === 'facturas' && <Facturas />}
      {tab === 'liquidaciones' && <Liquidaciones />}
    </div>
  )
}

function Kpi({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 ${warn ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'}`}>
      <div className="text-xs text-slate-500 uppercase">{label}</div>
      <div className="text-xl font-bold text-slate-800 dark:text-slate-100 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  )
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${active ? 'border-teal-600 text-teal-600 dark:text-teal-400' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
      {children}
    </button>
  )
}

function CarrierBadge({ carrier }: { carrier?: string | null }) {
  if (!carrier) return <span className="text-xs text-slate-400">sin envío</span>
  const c = CARRIERS[carrier] || { label: carrier.toUpperCase(), color: 'bg-slate-100 text-slate-700' }
  return <span className={`text-xs font-medium px-2 py-0.5 rounded ${c.color}`}>{c.label}</span>
}

function StatusPill({ s }: { s: FacturaCodStatus }) {
  const c = STATUS[s]
  return <span className={`text-xs font-medium px-2 py-0.5 rounded whitespace-nowrap ${c.color}`}>{c.label}</span>
}

function Card({ title, subtitle, right, children }: { title: string; subtitle?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden mb-4">
      <div className="px-4 py-2.5 flex items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-700 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</div>
          {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Vista 1: Avisos — lo que no cuadra o no se ha cobrado
// --------------------------------------------------------------------------- //
function Avisos() {
  const q = useQuery({
    queryKey: ['reembolsos-facturas', 'avisos'],
    queryFn: () => reembolsos.facturas(['impago', 'devuelta', 'diferencia']),
  })
  const rev = useQuery({ queryKey: ['reembolsos-revision'], queryFn: reembolsos.revision })
  if (q.isLoading || rev.isLoading) return <Loading />
  const all = q.data?.facturas || []
  const impagos = all.filter((f) => f.status === 'impago')
  const devueltas = all.filter((f) => f.status === 'devuelta')
  const diferencias = all.filter((f) => f.status === 'diferencia')
  const lines = rev.data?.lines || []
  const sum = (xs: FacturaCod[]) => eur(xs.reduce((a, b) => a + (b.importe || 0), 0))

  if (!all.length && !lines.length) {
    return (
      <>
        <Empty text="Todo cuadra: no hay impagos, devoluciones pendientes ni diferencias. 🎉" />
        <div className="flex justify-end"><ResueltosToggle kind="factura" /></div>
      </>
    )
  }
  return (
    <>
      {impagos.length > 0 && (
        <Card title={`Impagos · ${impagos.length} · ${sum(impagos)}`}
          subtitle="Facturas contra reembolso sin liquidación del transportista pasado el plazo. No caducan: siguen aquí hasta que se liquiden o se resuelvan."
          right={<ResueltosToggle kind="factura" />}>
          <FacturasTable rows={impagos} resolvable />
        </Card>
      )}
      {devueltas.length > 0 && (
        <Card title={`Envíos devueltos sin rectificativa · ${devueltas.length} · ${sum(devueltas)}`}
          subtitle="El envío volvió al almacén y no hay liquidación: falta la rectificativa en Factusol (o el reenvío).">
          <FacturasTable rows={devueltas} resolvable />
        </Card>
      )}
      {diferencias.length > 0 && (
        <Card title={`Liquidado con importe distinto · ${diferencias.length}`}
          subtitle="El transportista liquidó un importe que no coincide con la factura (reembolso de la etiqueta mal puesto, pago parcial…).">
          <FacturasTable rows={diferencias} resolvable showLiquidado />
        </Card>
      )}
      {lines.length > 0 && (
        <Card title={`Pagos del transportista que no casan con ninguna factura · ${lines.length}`}
          subtitle="Líneas de liquidación sin factura contra reembolso identificable (envío hecho fuera de la app, factura con otra forma de pago…)."
          right={<ResueltosToggle kind="revision" />}>
          <LinesTable lines={lines} resolvable />
        </Card>
      )}
    </>
  )
}

function FacturasTable({ rows, resolvable, showLiquidado, showStatus }: {
  rows: FacturaCod[]; resolvable?: boolean; showLiquidado?: boolean; showStatus?: boolean
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-900/40">
            <th className="px-3 py-2 text-left">Factura</th>
            <th className="px-3 py-2 text-left">Fecha</th>
            <th className="px-3 py-2 text-left">Cliente</th>
            <th className="px-3 py-2 text-left">Pedido</th>
            <th className="px-3 py-2 text-left">Envío</th>
            <th className="px-3 py-2 text-right">Días</th>
            <th className="px-3 py-2 text-right">Importe</th>
            {showLiquidado && <th className="px-3 py-2 text-right">Liquidado</th>}
            {showStatus && <th className="px-3 py-2 text-left">Estado</th>}
            {resolvable && <th className="px-3 py-2 text-right">Acción</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.key} className="border-t border-slate-100 dark:border-slate-700/50">
              <td className="px-3 py-2 font-mono whitespace-nowrap">{f.codfac}<span className="text-slate-400 text-xs">/{f.ejercicio.slice(2)}</span></td>
              <td className="px-3 py-2 whitespace-nowrap">{fdate(f.fecha)}</td>
              <td className="px-3 py-2 truncate max-w-[14rem]" title={f.cliente || ''}>{f.cliente}</td>
              <td className="px-3 py-2 font-mono">{f.order_ref_int || '—'}</td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <CarrierBadge carrier={f.carrier} />
                  {f.tracking && <span className="font-mono text-xs text-slate-500">{f.tracking}</span>}
                  {f.shipment_status && f.shipment_status !== 'delivered' && (
                    <span className="text-xs text-slate-400">({f.shipment_status})</span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 text-right text-slate-500">{f.age_days}</td>
              <td className="px-3 py-2 text-right font-medium">
                {eur(f.importe)}
                {Math.abs(f.importe_esperado - f.importe) > 0.01 && (
                  <div className="text-xs text-slate-400">a cobrar {eur(f.importe_esperado)}</div>
                )}
              </td>
              {showLiquidado && <td className="px-3 py-2 text-right text-amber-700">{f.liquidado != null ? eur(f.liquidado) : '—'}</td>}
              {showStatus && <td className="px-3 py-2"><StatusPill s={f.status} /></td>}
              {resolvable && <td className="px-3 py-2 text-right"><ResolveMenu kind="factura" itemKey={f.key} /></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Vista 2: Facturas — buscador por estado
// --------------------------------------------------------------------------- //
const FILTERS: { label: string; states: FacturaCodStatus[] }[] = [
  { label: 'Pendientes (en plazo)', states: ['en_gracia'] },
  { label: 'Con aviso', states: ['impago', 'devuelta', 'diferencia'] },
  { label: 'Cobradas (liquidación)', states: ['cobrada'] },
  { label: 'Cobradas (histórico Factusol)', states: ['cobrada_historico'] },
  { label: 'Resueltas a mano', states: ['resuelta'] },
  { label: 'Anuladas / excluidas', states: ['anulada', 'excluida'] },
]

function Facturas() {
  const [fi, setFi] = useState(0)
  const [text, setText] = useState('')
  const q = useQuery({
    queryKey: ['reembolsos-facturas', FILTERS[fi].states.join(',')],
    queryFn: () => reembolsos.facturas(FILTERS[fi].states),
  })
  const rows = useMemo(() => {
    const list = [...(q.data?.facturas || [])].reverse()
    const needle = text.trim().toLowerCase()
    if (!needle) return list
    return list.filter((f) =>
      [f.codfac, f.order_ref_int, f.cliente, f.tracking].some((v) => String(v ?? '').toLowerCase().includes(needle)))
  }, [q.data, text])
  const shown = rows.slice(0, 500)
  return (
    <Card
      title={`${rows.length} facturas · ${eur(rows.reduce((a, b) => a + (b.importe || 0), 0))}`}
      subtitle={rows.length > 500 ? 'Se muestran las 500 más recientes; usa el buscador para acotar.' : undefined}
      right={
        <div className="flex items-center gap-2 flex-wrap">
          <select value={fi} onChange={(e) => setFi(Number(e.target.value))}
            className="text-sm border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-700">
            {FILTERS.map((f, i) => <option key={f.label} value={i}>{f.label}</option>)}
          </select>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2 top-2" />
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Factura, pedido, cliente, tracking"
              className="text-sm border border-slate-300 dark:border-slate-600 rounded-lg pl-8 pr-2 py-1.5 bg-white dark:bg-slate-700 w-64" />
          </div>
        </div>
      }>
      {q.isLoading ? <Loading /> : !shown.length ? <Empty text="Sin facturas en este estado." /> :
        <FacturasTable rows={shown} showLiquidado showStatus resolvable={FILTERS[fi].states.includes('impago') || FILTERS[fi].states.includes('en_gracia')} />}
    </Card>
  )
}

// --------------------------------------------------------------------------- //
// Vista 3: Liquidaciones con tick de banco y detalle expandible
// --------------------------------------------------------------------------- //
function Liquidaciones() {
  const toast = useToast()
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState<number | null>(null)
  const [carrier, setCarrier] = useState<string>('')
  const q = useQuery({ queryKey: ['reembolsos-batches', carrier], queryFn: () => reembolsos.listBatches(carrier || undefined, 300) })

  const bankMut = useMutation({
    mutationFn: ({ id, collected }: { id: number; collected: boolean }) =>
      reembolsos.setBankCollected(id, { collected }),
    onSuccess: () => invalidateAll(qc),
    onError: (e: Error) => toast.error(`Error: ${e.message}`),
  })

  const batches = q.data?.batches || []
  return (
    <Card title="Liquidaciones recibidas"
      subtitle="Marca «cobrada en banco» cuando la transferencia aparezca en el extracto."
      right={
        <select value={carrier} onChange={(e) => setCarrier(e.target.value)}
          className="text-sm border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-700">
          <option value="">Todos los transportistas</option>
          <option value="mrw">MRW</option>
          <option value="gls">GLS</option>
          <option value="cex">Correos Express</option>
        </select>
      }>
      {q.isLoading ? <Loading /> : !batches.length ? <Empty text="No hay liquidaciones registradas todavía." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700">
                <th className="px-3 py-2 text-left w-8"></th>
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-left">Transportista</th>
                <th className="px-3 py-2 text-left">Documento</th>
                <th className="px-3 py-2 text-center">Reembolsos</th>
                <th className="px-3 py-2 text-right">A ingresar</th>
                <th className="px-3 py-2 text-center">Casado con facturas</th>
                <th className="px-3 py-2 text-center">Cobrada en banco</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <BatchRow key={b.id} b={b} expanded={expanded === b.id}
                  onToggle={() => setExpanded(expanded === b.id ? null : b.id)}
                  onBank={(collected) => bankMut.mutate({ id: b.id, collected })}
                  bankBusy={bankMut.isPending} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function BatchRow({ b, expanded, onToggle, onBank, bankBusy }: {
  b: ReembolsoBatch; expanded: boolean; onToggle: () => void; onBank: (c: boolean) => void; bankBusy: boolean
}) {
  const detail = useQuery({
    queryKey: ['reembolsos-batch', b.id],
    queryFn: () => reembolsos.batchDetail(b.id),
    enabled: expanded,
  })
  const old = !b.bank_collected && b.settlement_date && (Date.now() - new Date(b.settlement_date).getTime()) > 7 * 86400000
  return (
    <>
      <tr className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/20">
        <td className="px-3 py-2 cursor-pointer" onClick={onToggle}>
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </td>
        <td className="px-3 py-2 cursor-pointer whitespace-nowrap" onClick={onToggle}>{fdate(b.settlement_date || b.email_date)}</td>
        <td className="px-3 py-2"><CarrierBadge carrier={b.carrier} /></td>
        <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[14rem]" title={b.document_ref || ''}>{b.document_ref}</td>
        <td className="px-3 py-2 text-center">{b.line_count}</td>
        <td className="px-3 py-2 text-right font-medium">{eur(b.total_amount)}</td>
        <td className="px-3 py-2 text-center whitespace-nowrap">
          <span className="text-green-600">{b.ok_count}✓</span>
          {b.diferencia_count > 0 && <span className="text-amber-600 ml-1">{b.diferencia_count}⚠</span>}
          {b.sin_factura_count > 0 && <span className="text-slate-400 ml-1">{b.sin_factura_count}?</span>}
        </td>
        <td className="px-3 py-2 text-center">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={b.bank_collected === 1} disabled={bankBusy}
              onChange={(e) => onBank(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer" />
            {b.bank_collected === 1 && b.bank_collected_date && (
              <span className="text-xs text-green-600">{fdate(b.bank_collected_date)}</span>
            )}
            {old && <span className="text-xs text-amber-600">+7 días</span>}
          </label>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} className="bg-slate-50 dark:bg-slate-900/30 px-6 py-3">
            {detail.isLoading ? <Loading /> : <LinesTable lines={detail.data?.lines || []} compact />}
          </td>
        </tr>
      )}
    </>
  )
}

function LinesTable({ lines, resolvable, compact }: { lines: ReembolsoLine[]; resolvable?: boolean; compact?: boolean }) {
  if (!lines.length) return <div className="text-slate-400 text-sm py-2">Sin líneas.</div>
  return (
    <div className="overflow-x-auto">
      <table className={`w-full ${compact ? 'text-xs' : 'text-sm'}`}>
        <thead>
          <tr className="text-xs text-slate-500 uppercase">
            {!compact && <th className="px-2 py-1.5 text-left">Fecha</th>}
            {!compact && <th className="px-2 py-1.5 text-left">Transportista</th>}
            <th className="px-2 py-1.5 text-left">Ref. / tracking</th>
            <th className="px-2 py-1.5 text-left">Destinatario</th>
            <th className="px-2 py-1.5 text-right">Liquidado</th>
            <th className="px-2 py-1.5 text-left">Factura(s)</th>
            <th className="px-2 py-1.5 text-right">Importe factura</th>
            <th className="px-2 py-1.5 text-center">Estado</th>
            {resolvable && <th className="px-2 py-1.5 text-right">Acción</th>}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id} className="border-t border-slate-200 dark:border-slate-700/40">
              {!compact && <td className="px-2 py-1.5 whitespace-nowrap">{fdate(l.settlement_date)}</td>}
              {!compact && <td className="px-2 py-1.5"><CarrierBadge carrier={l.carrier} /></td>}
              <td className="px-2 py-1.5 font-mono">{l.order_reference || l.tracking}</td>
              <td className="px-2 py-1.5 truncate max-w-xs" title={l.recipient || ''}>{l.recipient}</td>
              <td className="px-2 py-1.5 text-right">{eur(l.settled_amount)}</td>
              <td className="px-2 py-1.5 font-mono">{l.invoices.length ? l.invoices.map((k) => k.split('-')[1]).join(' + ') : '—'}</td>
              <td className="px-2 py-1.5 text-right text-slate-500">
                {l.invoice_total != null ? eur(l.invoice_total) : '—'}
                {l.cod_etiqueta != null && Math.abs((l.cod_etiqueta || 0) - (l.invoice_total || 0)) > 0.01 && (
                  <div className="text-[11px] text-slate-400">etiqueta pedía {eur(l.cod_etiqueta)}</div>
                )}
              </td>
              <td className="px-2 py-1.5 text-center"><LineStatus s={l.status} /></td>
              {resolvable && <td className="px-2 py-1.5 text-right"><ResolveMenu kind="revision" itemKey={l.id} /></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function LineStatus({ s }: { s: ReembolsoLine['status'] }) {
  if (s === 'ok') return <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle2 className="w-3.5 h-3.5" />Cuadra</span>
  if (s === 'diferencia') return <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3.5 h-3.5" />Importe distinto</span>
  if (s === 'duplicada') return <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3.5 h-3.5" />Duplicada</span>
  return <span className="inline-flex items-center gap-1 text-slate-400"><HelpCircle className="w-3.5 h-3.5" />Sin factura</span>
}

const FACTURA_REASONS = [
  { k: 'cobrado_otra_via', label: 'Cobrado por otra vía (transferencia, tienda…)' },
  { k: 'reclamado', label: 'Reclamado al transportista' },
  { k: 'rectificada', label: 'Rectificada / no se debía cobrar' },
  { k: 'perdido', label: 'Dado por perdido' },
  { k: 'ignorar', label: 'Ignorar (error / no aplica)' },
]
const REVISION_REASONS = [
  { k: 'revisado', label: 'Revisado, correcto' },
  { k: 'factura_otra_forma_pago', label: 'Factura con otra forma de pago' },
  { k: 'ignorar', label: 'Ignorar (error / no aplica)' },
]

function ResolveMenu({ kind, itemKey }: { kind: 'factura' | 'revision'; itemKey: string | number }) {
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const toast = useToast()
  const qc = useQueryClient()
  const reasons = kind === 'factura' ? FACTURA_REASONS : REVISION_REASONS
  const mut = useMutation({
    mutationFn: (status: string) => reembolsos.resolve(kind, itemKey, status, note || undefined),
    onSuccess: () => {
      toast.success('Marcado como resuelto')
      invalidateAll(qc)
    },
    onError: (e: Error) => toast.error(`Error: ${e.message}`),
  })
  return (
    <>
      <button onClick={() => setOpen(true)}
        className="px-2 py-1 text-xs font-medium text-teal-700 dark:text-teal-400 border border-teal-200 dark:border-teal-800 rounded hover:bg-teal-50 dark:hover:bg-teal-900/20">
        Resolver
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          onClick={() => setOpen(false)}>
          <div className="w-full max-w-sm bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 p-4 text-left"
            onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-3">Marcar como resuelto</div>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (opcional)"
              className="w-full mb-3 text-sm border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-700" />
            <div className="space-y-1.5">
              {reasons.map((r) => (
                <button key={r.k} onClick={() => { mut.mutate(r.k); setOpen(false) }}
                  className="block w-full text-left px-3 py-2 text-sm rounded-lg text-slate-700 dark:text-slate-200 border border-slate-100 dark:border-slate-700 hover:bg-teal-50 hover:border-teal-200 dark:hover:bg-teal-900/20">
                  {r.label}
                </button>
              ))}
            </div>
            <button onClick={() => setOpen(false)} className="mt-3 w-full text-xs text-slate-400 hover:text-slate-600">Cancelar</button>
          </div>
        </div>
      )}
    </>
  )
}

function ResueltosToggle({ kind }: { kind: 'factura' | 'revision' }) {
  const [open, setOpen] = useState(false)
  const qc = useQueryClient()
  const toast = useToast()
  const q = useQuery({ queryKey: ['reembolsos-resolved', kind], queryFn: () => reembolsos.resolved(kind), enabled: open })
  const undo = useMutation({
    mutationFn: (key: string) => reembolsos.unresolve(kind, key),
    onSuccess: () => {
      toast.success('Resolución deshecha')
      invalidateAll(qc)
    },
  })
  const rows = q.data?.resolved || []
  return (
    <div className="text-xs">
      <button onClick={() => setOpen(!open)} className="text-slate-500 hover:text-slate-700 underline">
        {open ? 'ocultar resueltos' : 'ver resueltos'}
      </button>
      {open && (
        <div className="mt-2 border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-100 dark:divide-slate-700">
          {!rows.length ? <div className="px-3 py-2 text-slate-400">Nada resuelto manualmente.</div> :
            rows.map((r: any) => (
              <div key={r.ref_key} className="flex items-center justify-between gap-3 px-3 py-1.5">
                <span className="text-slate-600 dark:text-slate-300">
                  <span className="font-mono">{r.ref_key}</span> · {r.status || '—'} {r.note ? `· ${r.note}` : ''} <span className="text-slate-400">({fdate(r.resolved_at)})</span>
                </span>
                <button onClick={() => undo.mutate(r.ref_key)} className="inline-flex items-center gap-1 text-teal-700 dark:text-teal-400 hover:underline">
                  <Undo2 className="w-3 h-3" /> deshacer
                </button>
              </div>
            ))}
        </div>
      )}
    </div>
  )
}

function Loading() {
  return <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" />Cargando…</div>
}
function Empty({ text }: { text: string }) {
  return <div className="flex flex-col items-center justify-center py-12 text-slate-400"><Package className="w-8 h-8 mb-2 opacity-40" />{text}</div>
}
